import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'

import { GridColDef } from '@mui/x-data-grid'

import { memberListMemberQueryOptions } from '@/api/listQueryOptions'
import { ListMemberType } from '@/api/lists'
import {
    defaultMemberSection
} from '@/api/mem/memberSection'
import EditGrid, { useEditGrid } from '@/components/detail/EditGrid'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import useResponsive from '@/components/hooks/useResponsive'
import { getColorByBgColor } from '@/lib/colors'
import { getOrganization } from '@/store/auth/authSlice'
import { Chip } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { t } from 'i18next'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/lists'
)({
    component: MemberListComponent,
})

function MemberListComponent() {
    const params = Route.useParams()
    const listMembersQuery = useSuspenseQuery(
        memberListMemberQueryOptions(params.memberUuid),
    )
    const listMembers = listMembersQuery.data

    const basicGridColumns = useBasicGridColumns()
    const sessionOrganization = useSelector(getOrganization);

    const { xsmall, small } = useResponsive()

    const listTypes = [
        { value: 'CAMP', label: t('enum.listType.CAMP') },
        { value: 'HEIMSTUNDE', label: t('enum.listType.HEIMSTUNDE') },
        { value: 'EDUCATION', label: t('enum.listType.EDUCATION') },
        { value: 'CONTACTGROUP', label: t('enum.listType.CONTACTGROUP') },
        { value: 'MEETING', label: t('enum.listType.MEETING') },
        { value: 'APPLICATION', label: t('enum.listType.APPLICATION') },
        { value: 'OTHER', label: t('enum.listType.OTHER') },
    ]

    const columns: GridColDef[] = []
    columns.push({
        field: 'listHeader.name',
        valueGetter: (_value, row) => `${row.listHeader.name}`,
        headerName: 'Liste',
        minWidth: 250,
    })

    columns.push({
        field: 'listHeader.type',
        valueGetter: (_value, row) => `${row.listHeader.type}`,
        type: 'singleSelect',
        valueOptions: listTypes,
        headerName: 'Typ',
        minWidth: 100,
    })

    columns.push({
        field: 'listHeader.organization',
        valueGetter: (_value, row) => `${row.listHeader.organization}`,
        renderCell: (params) => {
            const organization = params.row.organization;

            return (
                <Tooltip title={organization.name}>
                    <Chip color="secondary" style={{ color: getColorByBgColor(sessionOrganization?.color), backgroundColor: organization.dissolvedYear ? 'lightgray' : organization.color }} label={organization.organizationSystem == 'GILDE' ? organization.name : organization.abbreviation} />
                </Tooltip>
            )
        },
        type: 'singleSelect',
        filterable: false,
        headerName: 'Organisation',
        minWidth: 150,
        headerAlign: 'center',
        align: 'center',
    })

    columns.push({
        field: 'listHeader.active',
        valueGetter: (_value, row) => `${row.listHeader.active}`,
        headerName: 'Aktiv',
        minWidth: 50,
        type: 'boolean',
    })

    if (xsmall || small) {
        columns.push(basicGridColumns.validPeriodColumn)
    } else {
        columns.push(
            basicGridColumns.startDateColumn,
            basicGridColumns.endDateColumn,
        )

    }

    const editGrid = useEditGrid<ListMemberType>({
        defaultValues: defaultMemberSection,
        updateMutation: undefined,
        deleteMutation: undefined,
        records: listMembers,
        columns: columns,
        title: t('entity.listMember'),
    })

    if (listMembersQuery.isLoading || listMembersQuery.isFetching) {
        return <LoadProgress />
    }

    return (<>
        <EditGrid {...editGrid} parentUuid={(entity) => entity.member.uuid} />
    </>);
}
